import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "../hooks/useSEO";
import axios from "axios";
import { useAuth } from "../App";
import { API } from "../config";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Play, ThumbsUp, ThumbsDown, LogOut, User, Menu, X, Shield, Info, ArrowRight, Heart, Users } from "lucide-react";
import NotificationBell from "../components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { ShareButton } from "../components/ShareButton";

// New Section Imports
import { HeroSection } from "../components/landing/HeroSection";
import { WhyZeelSection } from "../components/landing/WhyZeelSection";
import { FoundingReviewers } from "../components/landing/FoundingReviewers";
import { RoutineCardsSection } from "../components/landing/RoutineCardsSection";
import { SeeWhatWorks } from "../components/landing/SeeWhatWorks";
import { HowItWorksSection } from "../components/landing/HowItWorksSection";
import { CommunityRewards } from "../components/landing/CommunityRewards";
import { FinalCTA } from "../components/landing/FinalCTA";

import { Navbar } from "../components/Navbar";

const LOGO_URL = "https://uxdesignlabs.com/wp-content/uploads/2024/07/zeel-logo.svg";

// ─── Product Card (reused in Products section) ──────────
const getTrustLabel = (score, displayReady) => {
  if (!displayReady || score === null) return null;
  if (score >= 70) return { label: "High Community Confidence", color: "bg-teal-50 text-teal-700", dot: "bg-teal-500" };
  if (score >= 50) return { label: "Moderate Confidence", color: "bg-blue-50 text-blue-700", dot: "bg-blue-500" };
  if (score >= 30) return { label: "Low Confidence", color: "bg-amber-50 text-amber-700", dot: "bg-amber-500" };
  return { label: "Community Caution", color: "bg-rose-50 text-rose-700", dot: "bg-rose-500" };
};

const ProductCard = ({ product }) => {
  const zcs = product.zcs;
  const displayReady = zcs?.display_ready;
  const score = zcs?.zcs;
  const trustLabel = getTrustLabel(score, displayReady);
  const totalYes = zcs?.total_yes_votes || 0;
  const totalNo = zcs?.total_no_votes || 0;

  return (
    <Link to={`/product/${product.slug || product.product_id}`} data-testid={`product-card-${product.product_id}`}>
      <Card className="product-card bg-white border border-slate-100 hover:border-blue-200 rounded-2xl overflow-hidden h-full">
        <div className="aspect-square relative overflow-hidden bg-slate-50 flex items-center justify-center p-4">
          <img
            src={product.image_url}
            alt={product.name}
            className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=400"; }}
          />
        </div>
        <CardContent className="p-5">
          {product.brand && (
            <p className="text-[11px] font-medium text-[#5B6BA8] mb-1 tracking-wide uppercase">{product.brand}</p>
          )}
          <h3 className="font-semibold text-foreground mb-2 line-clamp-2">{product.name}</h3>
          <div className="space-y-1.5 mb-3">
            {displayReady && score !== null ? (
              <>
                <div className="flex items-center justify-between">
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-[11px] text-muted-foreground font-medium flex items-center gap-1 cursor-help">
                          Community Confidence Score <Info className="w-3 h-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[220px] text-center">
                        Based on how the community voted on product claims. Higher % = more positive experiences.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <span className={`text-base font-bold ${score >= 80 ? 'text-emerald-600' : score >= 60 ? 'text-amber-500' : 'text-rose-500'}`}>{score}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(score, 100)}%`, background: 'linear-gradient(90deg, rgba(59,90,198,0.65), rgba(107,79,168,0.65), rgba(168,85,160,0.6))' }} />
                </div>
                {trustLabel && score >= 70 && (
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${trustLabel.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${trustLabel.dot}`} />
                    {trustLabel.label}
                  </span>
                )}
              </>
            ) : (
              <span className="text-xs text-muted-foreground">
                {zcs?.total_votes > 0 ? "Still Building Data" : "New product"}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1"><Play className="w-3.5 h-3.5" /><span>{product.video_count}</span></div>
              <div className="flex items-center gap-1" data-testid={`card-upvotes-${product.product_id}`}><ThumbsUp className="w-3.5 h-3.5" /><span>{totalYes}</span></div>
              <div className="flex items-center gap-1" data-testid={`card-downvotes-${product.product_id}`}><ThumbsDown className="w-3.5 h-3.5" /><span>{totalNo}</span></div>
            </div>
            <ShareButton product={product} zcs={zcs} />
          </div>
          <Button variant="secondary" className="w-full rounded-full text-sm font-medium" data-testid={`view-validation-btn-${product.product_id}`}>
            View Reviews
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
};

// ─── Footer ─────────────────────────────────────────────
const Footer = () => (
  <footer className="relative overflow-hidden" data-testid="footer" style={{ background: 'linear-gradient(150deg, #4953A6 0%, #4F53A6 20%, #5454A6 35%, #5854A5 50%, #6454A4 65%, #7655A4 78%, #8A55A3 90%, #9F56A3 100%)' }}>
    {/* Top content area */}
    <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
      <div className="flex flex-col md:flex-row md:justify-between gap-10">
        {/* Left — logo + description + social */}
        <div className="max-w-xs">
          <img src={LOGO_URL} alt="Zeel" className="h-[32px] w-auto object-contain brightness-0 invert mb-4" />
          <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-6">
            Skincare, figured out together. Advice from people who've been where you are.
          </p>
          <div className="flex items-center gap-4">
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors" data-testid="social-linkedin">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors" data-testid="social-youtube">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors" data-testid="social-facebook">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors" data-testid="social-instagram">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            </a>
            <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors" data-testid="social-x">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
          </div>
        </div>

        {/* Right — nav links */}
        <div className="flex flex-col gap-2.5">
          <span className="text-sm font-medium text-white/80 hover:text-white cursor-pointer transition-colors">About Us</span>
          <span className="text-sm font-medium text-white/80 hover:text-white cursor-pointer transition-colors">Who We Are</span>
          <span className="text-sm font-medium text-white/80 hover:text-white cursor-pointer transition-colors">The Zeel Community</span>
          <span className="text-sm font-medium text-white/80 hover:text-white cursor-pointer transition-colors">Contact Us</span>
          <span className="text-sm font-medium text-white/80 hover:text-white cursor-pointer transition-colors">Terms of Service</span>
          <span className="text-sm font-medium text-white/80 hover:text-white cursor-pointer transition-colors">Privacy Policy</span>
        </div>
      </div>
    </div>

    {/* Email + legal row */}
    <div className="max-w-7xl mx-auto px-6 pb-10">
      <div className="flex flex-col items-center gap-6">
        <a href="mailto:hello@zeel.beauty" className="text-2xl sm:text-3xl font-semibold text-white hover:text-white/80 transition-colors tracking-tight" data-testid="footer-email">
          hello@zeel.beauty
        </a>
        <div className="text-xs text-white/40 text-center">
          <span>&copy; 2026 Overmoon Labs Pvt Ltd</span>
        </div>
      </div>
    </div>

    {/* Giant "zeel" wordmark — subliminal, top half visible, bottom half below fold */}
    <div className="relative h-[180px] sm:h-[240px] overflow-hidden select-none pointer-events-none" aria-hidden="true">
      <div className="absolute top-[-15%] left-1/2 -translate-x-1/2 whitespace-nowrap">
        <img src={LOGO_URL} alt="" className="h-[340px] sm:h-[460px] w-auto object-contain brightness-0 invert opacity-60" style={{ minWidth: '1100px' }} />
      </div>
    </div>
  </footer>
);

// ─── Main Landing Page ──────────────────────────────────
const LandingPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, login, updateSkinType } = useAuth();
  const [showSkinPicker, setShowSkinPicker] = useState(false);
  const userSkinType = user?.skin_type || null;

  useSEO({
    title: "Zeel Beauty — Skincare, Figured Out Together",
    description: "Zeel is a community-powered skincare platform. Share routines, vote on product claims, and discover what works for your skin type. Real people, real results.",
    ogImage: "https://zeel.beauty/og-image.jpg",
    canonical: "https://zeel.beauty/",
  });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        let url = `${API}/products?limit=8&sort=trust_score`;
        if (userSkinType) url += `&skin_type=${userSkinType}`;
        const response = await axios.get(url);
        setProducts(response.data.products || response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [userSkinType]);

  useEffect(() => {
    if (user && !user.skin_type) {
      const timer = setTimeout(() => setShowSkinPicker(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleSkinTypeSelect = async (type) => {
    await updateSkinType(type);
    setShowSkinPicker(false);
  };

  const formatSkinType = (st) => st?.replace(/\b\w/g, c => c.toUpperCase()) || "";

  const scrollToProducts = () => document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Navbar />

      {/* 1. Hero */}
      <HeroSection onExplore={scrollToProducts} />

      {/* 2. Routines shared by people like you */}
      <RoutineCardsSection />

      {/* 3. See What Actually Works (Product Reviews) */}
      <SeeWhatWorks />

      {/* 6. Popular Products */}
      <section id="products-section" className="py-20 px-4 bg-[#FAFBFC]" data-testid="products-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3 px-4 py-1.5 bg-[#5B6BA8]/[0.06] rounded-full">
              <Heart className="w-4 h-4 text-[#5B6BA8]" />
              <span className="text-sm font-medium text-[#5B6BA8]">Most Loved & Reviewed Products</span>
            </div>
            {user && userSkinType ? (
              <>
                <p className="text-sm font-medium text-[#5B6BA8] mb-3" data-testid="personalized-greeting">
                  Hey {user.name?.split(" ")[0]} — showing products for {formatSkinType(userSkinType)} Skin
                </p>
                <h2 className="text-2xl sm:text-3xl font-semibold mb-3 text-gradient-brand">Recommended for You</h2>
                <p className="text-muted-foreground text-sm sm:text-base">Products validated by others with {formatSkinType(userSkinType).toLowerCase()} skin.</p>
              </>
            ) : (
              <>
                <h2 className="text-2xl sm:text-3xl font-semibold mb-3 text-gradient-brand">Popular Products — Validated by Real Users</h2>
                <p className="text-muted-foreground text-sm sm:text-base">See what actually worked before you buy.</p>
              </>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden">
                  <div className="aspect-square skeleton-shimmer" />
                  <div className="p-5 space-y-3"><div className="h-5 skeleton-shimmer rounded" /><div className="h-4 skeleton-shimmer rounded w-2/3" /></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
              {products.map((product, index) => (
                <div key={product.product_id} className="animate-slide-up opacity-0" style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}>
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
          <div className="text-center mt-12">
            <Link to="/products">
              <Button variant="outline" size="lg" className="rounded-full px-10 h-12 text-base font-medium" data-testid="view-more-products-btn">
                View More Products <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. The People Behind Zeel */}
      <FoundingReviewers />

      {/* 6. Why Zeel Exists */}
      <WhyZeelSection />

      {/* 7. How Zeel Works */}
      <HowItWorksSection />

      {/* 8. Community & Rewards */}
      <CommunityRewards />

      {/* 9. Final CTA */}
      <FinalCTA />

      {/* 10. Footer */}
      <Footer />

      {/* Skin Type Picker Modal */}
      {showSkinPicker && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="skin-type-picker">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl animate-fade-in">
            <h3 className="text-xl font-semibold text-center mb-2">What's your skin type?</h3>
            <p className="text-sm text-muted-foreground text-center mb-6">We'll show you products validated by people with similar skin.</p>
            <div className="grid grid-cols-1 gap-2">
              {[
                { value: "oily", label: "Oily", desc: "Shiny, enlarged pores" },
                { value: "dry", label: "Dry", desc: "Tight, flaky patches" },
                { value: "combination", label: "Combination", desc: "Oily T-zone, dry cheeks" },
                { value: "normal", label: "Normal", desc: "Balanced, not too oily or dry" },
                { value: "sensitive", label: "Sensitive", desc: "Easily irritated, reactive" },
              ].map(type => (
                <button
                  key={type.value}
                  onClick={() => handleSkinTypeSelect(type.value)}
                  className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-[#5B6BA8]/30 hover:bg-[#5B6BA8]/5 transition-all text-left"
                  data-testid={`skin-pick-${type.value}`}
                >
                  <div>
                    <span className="font-medium text-sm">{type.label}</span>
                    <span className="text-xs text-muted-foreground ml-2">{type.desc}</span>
                  </div>
                </button>
              ))}
            </div>
            <button onClick={() => setShowSkinPicker(false)} className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors text-center" data-testid="skip-skin-type">
              Skip for now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
